import { createFileRoute, redirect, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { eq, isNull, or } from "drizzle-orm";
import { db, machines } from "@/db/index.server";
import { auth } from "@/lib/auth";
import { RecipeDetailsEdit } from "@/components/views/recipes/recipe-details-edit";
import { ContainerSmall } from "@/components/primitives/container-small";
import { PageContainer } from "@/components/primitives/layout";
import { transformRecipe } from "@/lib/recipes/transformRecipe";
import { sortMachines } from "@/components/views/calculator/utils";
import { getRecipeBySlug } from "@/lib/recipes/getRecipeBySlug";

const getEditRecipeData = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const request = getRequest();
    const session = await auth.api.getSession({ headers: request.headers });

    if (!session) return { redirect: true as const };

    const dbRecipe = await getRecipeBySlug(
      data.slug,
      session.user.id,
      "owner_only",
    );

    if (!dbRecipe) return { notFound: true as const };

    const recipe = transformRecipe(dbRecipe);
    const machinesData = await db
      .select()
      .from(machines)
      .where(
        or(eq(machines.createdBy, session.user.id), isNull(machines.createdBy)),
      );

    return {
      recipe,
      machines: sortMachines(machinesData),
      title: `Edit "${dbRecipe.name}" - Scoopulator`,
    };
  });

export const Route = createFileRoute("/recipes/$slug/edit")({
  loader: async ({ params }) => {
    const data = await getEditRecipeData({ data: { slug: params.slug } });
    if ("redirect" in data) throw redirect({ to: "/sign-in" });
    if ("notFound" in data) throw notFound();
    return data;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    return {
      meta: [{ title: loaderData.title }],
    };
  },
  component: RecipeEditPage,
});

function RecipeEditPage() {
  const { recipe, machines } = Route.useLoaderData();

  return (
    <PageContainer>
      <ContainerSmall>
        <RecipeDetailsEdit recipe={recipe} machines={machines} />
      </ContainerSmall>
    </PageContainer>
  );
}
